// BasicInformation.js - Refactored for Validation and UX

import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Typography,
  useTheme,
  alpha,
  Paper,
  IconButton,
  Tooltip,
  InputAdornment,
  Fade,
  Alert,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Cake as CakeIcon,
  LocationOn as LocationIcon,
  Help as HelpIcon,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { basicInfoSchema, validateField } from '../../../utils/validation';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  background: alpha(theme.palette.background.paper, 0.8),
  backdropFilter: 'blur(8px)',
  borderRadius: theme.shape.borderRadius * 2,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    boxShadow: theme.shadows[4],
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
    },
  },
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
    },
  },
}));

const BasicInformation = ({ data = {}, onChange, errors = {} }) => {
  const theme = useTheme();
  const [localErrors, setLocalErrors] = useState({});
  const [formattedPhone, setFormattedPhone] = useState('');
  const { availableOptions } = useSelector((state) => state.onboarding);
  const [validationSummary, setValidationSummary] = useState([]);

  // Debug log for initial data
  useEffect(() => {
    console.log('Initial data:', data);
    console.log('Initial phone:', data.phone);
  }, []);

  // Initialize formatted phone and validate when data loads
  useEffect(() => {
    console.log('Phone data changed:', data.phone);
    if (data.phone) {
      // Clean the phone number to only contain digits
      const cleanNumber = data.phone.replace(/\D/g, '');
      console.log('Cleaned phone number:', cleanNumber);
      
      // Format for display
      const formatted = formatPhoneNumber(cleanNumber);
      console.log('Formatted phone number:', formatted);
      setFormattedPhone(formatted);
      
      // Only set error if the number is invalid
      if (cleanNumber.length === 10) {
        setLocalErrors(prev => ({
          ...prev,
          phone: null
        }));
      }
    }
  }, [data.phone]);

  // Validate all fields when data changes
  useEffect(() => {
    if (data) {
      const newErrors = {};
      const summary = [];
      
      // Handle phone number formatting and validation
      if (data.phone) {
        const cleanNumber = data.phone.replace(/\D/g, '');
        const formatted = formatPhoneNumber(cleanNumber);
        setFormattedPhone(formatted);
        
        if (cleanNumber.length !== 10) {
          newErrors.phone = 'Please enter a valid 10-digit phone number';
          summary.push({
            field: 'Phone',
            error: 'Please enter a valid 10-digit phone number'
          });
        }
      } else {
        newErrors.phone = 'Phone number is required';
        summary.push({
          field: 'Phone',
          error: 'Phone number is required'
        });
      }

      // Validate other fields
      const fieldsToValidate = {
        fullName: 'Full Name',
        email: 'Email',
        gender: 'Gender',
        dateOfBirth: 'Date of Birth',
        address: 'Address'
      };

      Object.entries(fieldsToValidate).forEach(([field, label]) => {
        const value = data[field];
        if (!value || value.trim() === '') {
          newErrors[field] = `${label} is required`;
          summary.push({
            field: label,
            error: `${label} is required`
          });
        } else if (field === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          newErrors[field] = 'Please enter a valid email address';
          summary.push({
            field: label,
            error: 'Please enter a valid email address'
          });
        }
      });

      // Special handling for date of birth
      if (data.dateOfBirth) {
        const birthDate = new Date(data.dateOfBirth);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }

        if (age < 18) {
          newErrors.dateOfBirth = 'You must be at least 18 years old';
          summary.push({
            field: 'Date of Birth',
            error: 'You must be at least 18 years old'
          });
        }
      }
      
      setLocalErrors(newErrors);
      setValidationSummary(summary);
    }
  }, [data]);

  const formatPhoneNumber = (value) => {
    if (!value) return '';
    // Remove all non-digit characters
    const numbers = value.replace(/\D/g, '').slice(0, 10);
    // Format based on length
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(6)}`;
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    
    // Keep the original value with formatting for display
    setFormattedPhone(value);
    
    // Remove all non-digit characters for validation and submission
    const cleaned = value.replace(/\D/g, '');
    
    // Update the form data with the cleaned number
    onChange('basicInfo', { phone: cleaned });
    
    // Validate the phone number
    const error = validateField(basicInfoSchema, 'phone', cleaned);
    setLocalErrors(prev => ({
      ...prev,
      phone: error
    }));
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    onChange('basicInfo', { [field]: value });
    
    // Validate field in real-time using Zod schema
    const error = validateField(basicInfoSchema, field, value);
    setLocalErrors(prev => ({
      ...prev,
      [field]: error
    }));
  };

  const handleDateChange = (date) => {
    // Handle null or invalid date
    if (!date || isNaN(date.getTime())) {
      onChange('basicInfo', { dateOfBirth: null });
      setLocalErrors(prev => ({
        ...prev,
        dateOfBirth: 'Please select a valid date'
      }));
      return;
    }

    // Calculate age
    const today = new Date();
    const birthDate = new Date(date);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    // Validate age (minimum 18 years)
    if (age < 18) {
      setLocalErrors(prev => ({
        ...prev,
        dateOfBirth: 'You must be at least 18 years old'
      }));
      return;
    }

    // Format date as YYYY-MM-DD for backend
    const formattedDate = date.toISOString().split('T')[0];
    console.log('Formatted date for backend:', formattedDate);
    
    // Update both date fields to ensure consistency
    onChange('basicInfo', { 
      dateOfBirth: formattedDate,
      date_of_birth: formattedDate 
    });
    setLocalErrors(prev => ({
      ...prev,
      dateOfBirth: null
    }));
  };

  // Combine server errors with local validation errors
  const combinedErrors = { ...errors, ...localErrors };

  // Check if all required fields are filled
  const isFormValid = () => {
    const requiredFields = ['fullName', 'email', 'phone', 'gender', 'dateOfBirth', 'address'];
    return requiredFields.every(field => {
      const value = field === 'dateOfBirth' ? data[field] : data[field]?.trim();
      return value && value !== '';
    });
  };

  useEffect(() => {
    if (data) {
      const {
        fullName = '',
        email = '',
        phone = '',
        gender = '',
        dateOfBirth = '',
        address = ''
      } = data;

      setLocalErrors({
        fullName: validateField('fullName', fullName),
        email: validateField('email', email),
        phone: validateField('phone', phone),
        gender: validateField('gender', gender),
        dateOfBirth: validateField('date_of_birth', dateOfBirth),
        address: validateField('address', address)
      });

      // Create validation summary
      const summary = [];
      Object.entries({
        fullName: 'Full Name',
        email: 'Email',
        phone: 'Phone',
        gender: 'Gender',
        dateOfBirth: 'Date of Birth',
        address: 'Address'
      }).forEach(([field, label]) => {
        const error = validateField(field === 'dateOfBirth' ? 'date_of_birth' : field, data[field]);
        if (error) {
          summary.push({
            field: label,
            error: error
          });
        }
      });
      
      setValidationSummary(summary);
    }
  }, [data]);

  return (
    <Box>
      <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 3 }}>
        Personal Information
      </Typography>

      {validationSummary.length > 0 && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Please fix the following validation errors:
          </Typography>
          <ul style={{ margin: 0, paddingLeft: '1rem' }}>
            {validationSummary.map((item, index) => (
              <li key={index}>
                <Typography variant="body2">
                  <strong>{item.field}:</strong> {item.error}
                </Typography>
              </li>
            ))}
          </ul>
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <StyledPaper>
            <Typography variant="subtitle1" gutterBottom color="primary">
              Basic Details
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <StyledTextField
                  fullWidth
                  label="Full Name"
                  value={data.fullName || ''}
                  onChange={handleChange('fullName')}
                  error={!!combinedErrors.fullName}
                  helperText={combinedErrors.fullName}
                  required
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><PersonIcon color="primary" /></InputAdornment>
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <StyledTextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={data.email || ''}
                  onChange={handleChange('email')}
                  error={!!combinedErrors.email}
                  helperText={combinedErrors.email}
                  required
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><EmailIcon color="primary" /></InputAdornment>
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <StyledFormControl fullWidth>
                  <StyledTextField
                    value={formattedPhone}
                    onChange={handlePhoneChange}
                    placeholder="123-456-7890"
                    required
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><PhoneIcon color="primary" /></InputAdornment>,
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title="Enter a 10-digit phone number">
                            <IconButton size="small"><HelpIcon fontSize="small" /></IconButton>
                          </Tooltip>
                        </InputAdornment>
                      )
                    }}
                    label="Phone Number"
                    fullWidth
                    error={!!combinedErrors.phone}
                    helperText={combinedErrors.phone}
                  />
                </StyledFormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <StyledFormControl fullWidth error={!!combinedErrors.gender}>
                  <InputLabel>Gender</InputLabel>
                  <Select
                    value={data.gender || ''}
                    label="Gender"
                    onChange={handleChange('gender')}
                    required
                  >
                    {(availableOptions?.genders || [
                      { value: 'male', label: 'Male' },
                      { value: 'female', label: 'Female' },
                      { value: 'other', label: 'Other' }
                    ]).map((gender) => (
                      <MenuItem key={gender.value} value={gender.value}>{gender.label}</MenuItem>
                    ))}
                  </Select>
                  {combinedErrors.gender && <FormHelperText>{combinedErrors.gender}</FormHelperText>}
                </StyledFormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Date of Birth"
                    value={data.dateOfBirth ? new Date(data.dateOfBirth) : null}
                    onChange={handleDateChange}
                    maxDate={new Date(new Date().setFullYear(new Date().getFullYear() - 18))}
                    renderInput={(params) => (
                      <StyledTextField
                        {...params}
                        fullWidth
                        error={!!combinedErrors.dateOfBirth}
                        helperText={combinedErrors.dateOfBirth}
                        required
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: <InputAdornment position="start"><CakeIcon color="primary" /></InputAdornment>
                        }}
                      />
                    )}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>
          </StyledPaper>
        </Grid>

        <Grid item xs={12}>
          <StyledPaper>
            <Typography variant="subtitle1" gutterBottom color="primary">
              Address Information
            </Typography>
            <StyledTextField
              fullWidth
              label="Address"
              multiline
              rows={3}
              value={data.address || ''}
              onChange={handleChange('address')}
              error={!!combinedErrors.address}
              helperText={combinedErrors.address}
              required
              InputProps={{
                startAdornment: <InputAdornment position="start"><LocationIcon color="primary" /></InputAdornment>
              }}
            />
          </StyledPaper>
        </Grid>
      </Grid>

      <Fade in timeout={500}>
        <Box sx={{ mt: 3 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              We use this information to verify your identity and offer personalized services.
            </Typography>
          </Alert>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">Need help?</Typography>
            <Tooltip title="Contact support">
              <IconButton size="small" color="primary"><HelpIcon /></IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Fade>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 3, fontStyle: 'italic' }}>
        * All fields are required. Your data is secure and confidential.
      </Typography>
    </Box>
  );
};

export default BasicInformation;